import React, { useCallback, useEffect, useState } from "react";
import { apiGet, apiPost } from "../../utils/apiClient";
import "./MacroAnalysisPage.css";
import MacroCalibrationTool from "./MacroCalibrationTool";
import MacroOcrDebugViewer from "./MacroOcrDebugViewer";
import MinimapLabelTool from "./MinimapLabelTool";

type ClipClockRow = {
  videoTimeSec: number;
  gameClockSec: number | null;
  gameClockRawOcr: number | null;
  clockConfidence: number | null;
  timeModelPhase: string | null;
  timeModelDrift: number | null;
  ocrWasRun: boolean;
};

type GameTiming = {
  gameStartVideoSec: number | null;
  gameEndVideoSec: number | null;
  gameDurationSec: number | null;
};

type RunSummary = {
  points?: number;
  validationIssues?: number;
  extraction?: {
    timelineFrames: number;
    usableFrames: number;
    usableFrameRatio: number;
    avgDetectionConfidence: number;
  };
  model?: {
    inferenceMode: string;
    modelType: string;
    modelPath?: string | null;
    labelConfig?: Record<string, unknown> | null;
  };
};

function formatVideoTime(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/** In-game clock from OCR (same encoding as ingest: total seconds since game start). */
function formatGameClockSec(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/** Sanitized id for pipeline artifacts; matches backend clipIdToDefaultMatchId. */
function clipIdToDefaultMatchId(clipId: string): string {
  const s = clipId.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 120);
  return s || "macro_run";
}

type ClipListEntry = {
  clipId: string;
  source: string;
  framesPath: string;
  videoPath: string | null;
  url?: string;
  videoId?: string;
  updatedAt?: string;
};

const MacroAnalysisPage: React.FC = () => {
  const [matchId, setMatchId] = useState("");
  const [clipId, setClipId] = useState<string | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [clockRows, setClockRows] = useState<ClipClockRow[]>([]);
  const [gameTiming, setGameTiming] = useState<GameTiming | null>(null);
  const [clockRowsMessage, setClockRowsMessage] = useState<string | null>(null);
  const [issues, setIssues] = useState<any[]>([]);
  const [runSummary, setRunSummary] = useState<RunSummary | null>(null);
  const [ingestInfo, setIngestInfo] = useState<any>(null);
  const [trainingInfo, setTrainingInfo] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [clipsList, setClipsList] = useState<ClipListEntry[]>([]);
  const [clipsListError, setClipsListError] = useState<string | null>(null);
  const [macroRootsHint, setMacroRootsHint] = useState<string | null>(null);

  const refreshUploads = useCallback(async () => {
    try {
      setClipsListError(null);
      const data = await apiGet("/api/macro/uploads");
      setClipsList((data.clips as ClipListEntry[]) || []);
      const ur = typeof data.uploadsRoot === "string" ? data.uploadsRoot : "";
      const dr = typeof data.dataRoot === "string" ? data.dataRoot : "";
      setMacroRootsHint(ur && dr ? `${ur} · ${dr}` : ur || dr || null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load clip list from the API.";
      setClipsListError(msg);
      setClipsList([]);
      setMacroRootsHint(null);
    }
  }, []);

  useEffect(() => {
    void refreshUploads();
  }, [refreshUploads]);

  useEffect(() => {
    if (!clipId) {
      setClockRows([]);
      setGameTiming(null);
      setClockRowsMessage(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await apiGet(`/api/macro/clip-frames?clipId=${encodeURIComponent(clipId)}`);
        if (cancelled) return;
        setClockRows((data.rows as ClipClockRow[]) || []);
        setGameTiming((data.timing as GameTiming) ?? null);
        setClockRowsMessage(
          typeof data.count === "number" ? `${data.count} sampled frame(s)` : null,
        );
      } catch (e) {
        if (cancelled) return;
        setClockRows([]);
        setGameTiming(null);
        const msg = e instanceof Error ? e.message : "Failed to load clip frames";
        if (/\b404\b/.test(msg) || msg.toLowerCase().includes("no ingest")) {
          setClockRowsMessage("No ingest frames yet for this clip (upload or re-ingest).");
        } else {
          setClockRowsMessage(msg);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clipId, ingestInfo]);

  const runPipeline = async () => {
    if (!clipId) {
      setError("Select a clip from the list or upload a video first.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const runResponse = await apiPost("/api/macro/run", {
        matchId: matchId.trim() || clipIdToDefaultMatchId(clipId),
        clipId,
      });
      const validationData = await apiGet(`/api/macro/games/${matchId}/validation`);
      setIssues(validationData.issues || []);
      setRunSummary((runResponse?.summary as RunSummary) || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to run macro pipeline");
    } finally {
      setLoading(false);
    }
  };

  /** Deletes prior frame extraction + debug artifacts, re-ingests with saved calibration, then runs the macro pipeline. */
  const reingestAndRunPipeline = async () => {
    if (!clipId) {
      setError("Select or upload a clip first.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const reingest = await apiPost("/api/macro/reingest", { clipId });
      setIngestInfo(reingest.ingestResult ?? null);
      const runResponse = await apiPost("/api/macro/run", {
        matchId: matchId.trim() || clipIdToDefaultMatchId(clipId),
        clipId,
      });
      const validationData = await apiGet(`/api/macro/games/${matchId}/validation`);
      setIssues(validationData.issues || []);
      setRunSummary((runResponse?.summary as RunSummary) || null);
      void refreshUploads();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Re-ingest or pipeline run failed");
    } finally {
      setLoading(false);
    }
  };

  const uploadVideo = async () => {
    if (!videoFile) {
      setError("Please choose a video file first.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("video", videoFile);
      const response = await fetch("/api/macro/upload", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Upload failed (${response.status}): ${text}`);
      }
      const data = await response.json();
      const id = data.clipId ?? null;
      setClipId(id);
      if (id) {
        setMatchId(clipIdToDefaultMatchId(id));
      }
      setIngestInfo(data.ingestResult ?? null);
      void refreshUploads();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const trainModel = async () => {
    setLoading(true);
    setError(null);
    try {
      const trainResponse = await apiPost("/api/macro/train", {});
      setTrainingInfo(trainResponse?.trainingResult ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Training failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchFromYoutube = async () => {
    if (!youtubeUrl.trim()) {
      setError("Please enter a YouTube URL first.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await apiPost("/api/macro/upload-url", { url: youtubeUrl.trim() });
      const id = data.clipId ?? null;
      setClipId(id);
      if (id) {
        setMatchId(clipIdToDefaultMatchId(id));
      }
      setIngestInfo(data.ingestResult ?? null);
      void refreshUploads();
    } catch (err) {
      setError(err instanceof Error ? err.message : "YouTube fetch failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="macro-analysis-page">
      <h1>Macro Analysis MVP</h1>
      <p>
        Ingest video frames, align OCR/minimap regions, then inspect <strong>broadcast game time</strong> vs <strong>position in the video file</strong>{" "}
        (and optional pipeline validation).
      </p>
      <p className="macro-muted macro-workflow-note">
        <strong>Recommended workflow:</strong> obtain a high-resolution VOD file (e.g. 1080p) however you like, then <strong>upload it here</strong>. That
        keeps ingest stable while we tune OCR, minimap, and the rest of the pipeline—without depending on YouTube&apos;s changing download behavior.
      </p>

      <div className="macro-controls">
        <label htmlFor="matchId">Match ID (artifacts)</label>
        <input
          id="matchId"
          value={matchId}
          onChange={(e) => setMatchId(e.target.value)}
          placeholder="Defaults from clip id when you upload or pick a clip"
        />
        <input type="file" accept="video/*" onChange={(e) => setVideoFile(e.target.files?.[0] || null)} />
        <button type="button" onClick={uploadVideo} disabled={loading}>
          {loading ? "Uploading..." : "Upload video file"}
        </button>
        <button type="button" onClick={trainModel} disabled={loading}>
          {loading ? "Training..." : "Train Model"}
        </button>
        <button type="button" onClick={runPipeline} disabled={loading}>
          {loading ? "Running..." : "Run Macro Pipeline"}
        </button>
      </div>

      <div className="macro-optional-block">
        <h3>YouTube (optional)</h3>
        <p className="macro-muted">
          In-app fetch can break when YouTube or yt-dlp change. Prefer uploading a file until we revisit this.
        </p>
        <div className="macro-controls macro-controls-tight">
          <input
            type="url"
            placeholder="https://www.youtube.com/watch?v=..."
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
          />
          <button type="button" onClick={fetchFromYoutube} disabled={loading}>
            {loading ? "Fetching..." : "Fetch from YouTube"}
          </button>
        </div>
      </div>

      <p>{clipId ? `Active clip: ${clipId}` : "No clip selected—upload a video or pick one below."}</p>

      <div className="macro-controls">
        <button type="button" onClick={() => void refreshUploads()} disabled={loading}>
          Refresh uploaded clips
        </button>
      </div>
      <section className="macro-uploads-list">
        <h2>Existing clips</h2>
        <p className="macro-muted">Every upload or successful ingest with frames JSON appears here. Click to load calibration, clock table, and pipeline for that clip.</p>
        {macroRootsHint ? (
          <p className="macro-muted" title="Backend scan paths for *_frames.json">
            Scanned: {macroRootsHint}
          </p>
        ) : null}
        {clipsListError ? <div className="macro-error">Could not load clips list: {clipsListError}</div> : null}
        {clipsList.length === 0 && !clipsListError ? <p>No clips yet—upload a video to ingest.</p> : null}
        <ul className="macro-clips-ul">
          {clipsList.map((row) => (
            <li key={row.clipId}>
              <button
                type="button"
                className="macro-link-button"
                onClick={() => {
                  setClipId(row.clipId);
                  setMatchId(clipIdToDefaultMatchId(row.clipId));
                }}
              >
                {row.clipId}
              </button>{" "}
              <span className="macro-muted">
                {row.source === "upload" ? "file upload" : "youtube"}
                {row.videoId ? ` · videoId=${row.videoId}` : ""}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {error ? <div className="macro-error">{error}</div> : null}

      <div className="macro-summary">
        <div>Validation Issues: {issues.length}</div>
        <div>Inference Mode: {runSummary?.model?.inferenceMode ?? "heuristic"}</div>
      </div>

      <div className="macro-summary">
        <div>Timeline Frames: {runSummary?.extraction?.timelineFrames ?? 0}</div>
        <div>Usable Frames: {runSummary?.extraction?.usableFrames ?? 0}</div>
        <div>Usable Ratio: {((runSummary?.extraction?.usableFrameRatio ?? 0) * 100).toFixed(1)}%</div>
        <div>Avg Detection Confidence: {(runSummary?.extraction?.avgDetectionConfidence ?? 0).toFixed(3)}</div>
      </div>

      {runSummary?.model?.labelConfig ? (
        <div className="macro-summary">
          <div>Label Source: {(runSummary.model.labelConfig as any).label_source ?? "unknown"}</div>
          <div>Label Horizon: {(runSummary.model.labelConfig as any).horizon_seconds ?? "n/a"}s</div>
        </div>
      ) : null}

      {ingestInfo ? (
        <div className="macro-summary">
          <div>Ingest Schema: {ingestInfo.schema_version ?? "legacy"}</div>
          <div>Sampled Frames: {ingestInfo.sampled_frames ?? 0}</div>
        </div>
      ) : null}

      {trainingInfo?.metrics ? (
        <div className="macro-summary">
          <div>Train Loss: {Number(trainingInfo.metrics.train_loss ?? 0).toFixed(4)}</div>
          <div>Val Loss: {Number(trainingInfo.metrics.val_loss ?? 0).toFixed(4)}</div>
          <div>Val Accuracy: {Number(trainingInfo.metrics.val_accuracy ?? 0).toFixed(4)}</div>
        </div>
      ) : null}

      <MacroCalibrationTool clipId={clipId} matchId={matchId} busy={loading} onReingestAndRun={reingestAndRunPipeline} />

      <MacroOcrDebugViewer clipId={clipId} />

      <MinimapLabelTool clipId={clipId} />

      <div className="macro-grid">
        <section className="macro-clock-section">
          <h2>Game time (OCR) vs video time</h2>
          <p className="macro-muted">
            Each row is one sampled frame. <strong>Video time</strong> is where to seek in the file. <strong>Model time</strong> is the time model&apos;s
            authoritative game clock (predicted from elapsed time + sparse OCR confirmations). <strong>Raw OCR</strong> shows the actual Tesseract read when
            OCR was run on that frame. The <strong>Phase</strong> column shows the model state: searching, tracking, paused, or lost.
          </p>
          {clipId && gameTiming && gameTiming.gameStartVideoSec != null ? (
            <div className="macro-timing-summary">
              <div className="macro-timing-item">
                <span className="macro-timing-label">Game start</span>
                <span className="macro-timing-value">{formatVideoTime(gameTiming.gameStartVideoSec)} in video</span>
              </div>
              <div className="macro-timing-item">
                <span className="macro-timing-label">Game end</span>
                <span className="macro-timing-value">{gameTiming.gameEndVideoSec != null ? `${formatVideoTime(gameTiming.gameEndVideoSec)} in video` : "—"}</span>
              </div>
              <div className="macro-timing-item">
                <span className="macro-timing-label">Game duration</span>
                <span className="macro-timing-value">{gameTiming.gameDurationSec != null ? formatGameClockSec(gameTiming.gameDurationSec) : "—"}</span>
              </div>
            </div>
          ) : null}
          {clipId ? (
            <p className="macro-muted macro-clock-meta">{clockRowsMessage ?? "—"}</p>
          ) : (
            <p className="macro-muted">Select a clip to load ingest data.</p>
          )}
          <div className="macro-clock-table-wrap">
            <table className="macro-clock-table">
              <thead>
                <tr>
                  <th>Video time</th>
                  <th>Video (sec)</th>
                  <th>Model time</th>
                  <th>Model (sec)</th>
                  <th>Raw OCR</th>
                  <th>OCR (sec)</th>
                  <th>Conf.</th>
                  <th>Phase</th>
                  <th>Drift</th>
                </tr>
              </thead>
              <tbody>
                {clockRows.map((row, idx) => (
                  <tr key={`${row.videoTimeSec}_${idx}`} className={row.timeModelPhase === "paused" ? "macro-row-paused" : row.timeModelPhase === "lost" ? "macro-row-lost" : undefined}>
                    <td>{formatVideoTime(row.videoTimeSec)}</td>
                    <td>{row.videoTimeSec}</td>
                    <td>{row.gameClockSec == null ? "—" : formatGameClockSec(row.gameClockSec)}</td>
                    <td>{row.gameClockSec == null ? "—" : row.gameClockSec}</td>
                    <td>{row.ocrWasRun ? (row.gameClockRawOcr == null ? "—" : formatGameClockSec(row.gameClockRawOcr)) : ""}</td>
                    <td>{row.ocrWasRun ? (row.gameClockRawOcr == null ? "—" : row.gameClockRawOcr) : ""}</td>
                    <td>
                      {row.clockConfidence != null && !Number.isNaN(row.clockConfidence)
                        ? row.clockConfidence.toFixed(2)
                        : "—"}
                    </td>
                    <td>{row.timeModelPhase ?? "—"}</td>
                    <td>{row.timeModelDrift != null ? `${row.timeModelDrift > 0 ? "+" : ""}${row.timeModelDrift}s` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section>
          <h2>Validation</h2>
          {issues.length === 0 ? <div>No validation issues.</div> : null}
          {issues.map((issue, idx) => (
            <div key={`${issue.issue_type}_${idx}`} className="macro-row">
              <span>{issue.issue_type}</span>
              <span>{issue.message}</span>
              <span>{issue.severity}</span>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
};

export default MacroAnalysisPage;

